import { Button } from "components/DesignSystem/Button/Button";
import Async from "components/DesignSystem/AsyncComponents/Async";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { churnDetailsSchema } from "formValidations/churnDetailsSchema";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { ChurnDetailPayload, ChurnDetailResponse } from "store/apis/salesCrm";
import { OTHER_CHURN_REASON } from "constants/churnDetails";
import { ChurnDetailsForm } from "./ChurnDetailsForm";
import {
  CHURN_DETAIL_EDITABLE_FIELDS,
  ChurnDetailsFormValues,
  useChurnDetailsFormData,
} from "./useChurnDetailsFormData";

type ChurnDetailsModalProps = {
  isOpen: boolean;
  close: () => void;
  groupId: string;
  draftId?: string;
  prefetchedDraft?: ChurnDetailResponse;
  onSubmitted?: () => void;
  // Called after churn details are saved successfully so the caller can apply its own
  // CHURNED tag mutation -- see the comment on CHURNED in constants/TagType.ts. Returns
  // a message to fold into the save toast (e.g. "Company moved to archived"), or
  // undefined when there's nothing extra to report.
  onChurnConfirmed?: () => Promise<string | undefined>;
};

const buildPayload = (
  values: ChurnDetailsFormValues,
  draftId?: string
): ChurnDetailPayload => {
  const isOtherReason = values.churn_reason === OTHER_CHURN_REASON;
  const churnReason =
    isOtherReason && values.other_reason_detail
      ? values.other_reason_detail
      : values.churn_reason;

  return {
    customer_name: values.customer_name,
    joining_date: values.joining_date,
    churn_reason: churnReason,
    save_attempt_performed: values.save_attempt_performed,
    escalations_involved: values.escalations_involved,
    save_probability: values.save_probability,
    csm_score: values.csm_score,
    other_notes: values.other_notes,
    company_dissolved: values.company_dissolved,
    total_revenue_contribution: values.total_revenue_contribution,
    considered_churn: values.considered_churn,
    spoc_name: values.spoc_name.join(", "),
    products_subscribed: values.products_subscribed.join("\n"),
    filings_status: values.filings_status.join("\n"),
    request_spoc_to_fill: values.request_spoc_to_fill,
    ...(draftId && { draft_uuid: draftId }),
  };
};

export const ChurnDetailsModal = ({
  isOpen,
  close,
  groupId,
  draftId,
  prefetchedDraft,
  onSubmitted,
  onChurnConfirmed,
}: ChurnDetailsModalProps) => {
  const { successToast, alertToast } = useToast();
  const {
    isLoadingExistingData,
    hasLoadError,
    initialValues,
    productOptions,
    submitChurnDetail,
    isSubmitting,
    isReviewMode,
  } = useChurnDetailsFormData(isOpen, groupId, draftId, prefetchedDraft);

  const submitChurnDetails = async (
    values: ChurnDetailsFormValues,
    successMessage: string
  ) => {
    try {
      await submitChurnDetail({
        groupId,
        payload: buildPayload(values, draftId),
      }).unwrap();
      const archivalMessage = await onChurnConfirmed?.();
      const message = archivalMessage
        ? `${successMessage}. ${archivalMessage}.`
        : successMessage;
      successToast({ message });
      onSubmitted?.();
      close();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ??
          "Something went wrong",
      });
    }
  };

  const handleSubmit = (values: ChurnDetailsFormValues) =>
    submitChurnDetails(values, "Churn details saved");

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content size="xl" useCustomOverlay>
        <FormikForm<ChurnDetailsFormValues>
          initialValues={initialValues}
          enableReinitialize
          validationSchema={churnDetailsSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, submitForm, validateForm }) => {
            const handleRequestAmClick = () => {
              const delegatedValues = {
                ...values,
                request_spoc_to_fill: true,
              };
              CHURN_DETAIL_EDITABLE_FIELDS.forEach((field) => {
                delegatedValues[field] = "";
              });
              return submitChurnDetails(
                delegatedValues,
                "Requested AM successfully"
              );
            };

            const handleSubmitClick = async () => {
              const errors = await validateForm();
              if (Object.keys(errors).length > 0) {
                alertToast({
                  message:
                    "Fill in all the churn details, or check 'Request AM to fill in the details' before submitting.",
                });
                return;
              }
              await submitForm();
            };

            return (
              <>
                <Modal.Header>
                  <Modal.Title>
                    {isReviewMode ? "Review Churn Details" : "Churn Details"}
                  </Modal.Title>
                  <Modal.Close />
                </Modal.Header>
                <Modal.Body className="t-max-h-[60vh] t-overflow-y-scroll">
                  <Async.Root
                    isLoading={isLoadingExistingData}
                    isSuccess={!isLoadingExistingData && !hasLoadError}
                    isError={hasLoadError}
                    isEmpty={false}
                  >
                    <Async.ErrorHandler>
                      <div className="t-flex t-items-center t-justify-center t-p-7 t-text-body-sm t-text-text-30">
                        Something went wrong loading this record.
                      </div>
                    </Async.ErrorHandler>
                    <Async.Success>
                      <ChurnDetailsForm
                        values={values}
                        setFieldValue={setFieldValue}
                        initialValues={initialValues}
                        productOptions={productOptions}
                        isReviewMode={isReviewMode}
                        isSubmitting={isSubmitting}
                        onRequestAmClick={handleRequestAmClick}
                      />
                    </Async.Success>
                  </Async.Root>
                </Modal.Body>
                <Modal.FooterButtonGroup>
                  <Modal.RawClose asChild>
                    <Button type="button">Cancel</Button>
                  </Modal.RawClose>
                  <Button
                    type="button"
                    customType="primary"
                    isLoading={isSubmitting}
                    disabled={isLoadingExistingData || isSubmitting}
                    onClick={handleSubmitClick}
                  >
                    {isReviewMode ? "Save" : "Submit"}
                  </Button>
                </Modal.FooterButtonGroup>
              </>
            );
          }}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
